/**
 * VO system/room placement: persist ids + names; present names only.
 */

export type VoPlacementFields = {
  systemId: string | null;
  systemName: string | null;
  roomId: string | null;
  roomName: string | null;
  /** Legacy compose/display key — always a human system name when available. */
  systemKey: string | null;
  /** Legacy compose/display key — always a human room name when available (never opaque id). */
  roomKey: string | null;
};

export type DtoolsPlacementSource = {
  systemId?: string | null;
  systemName?: string | null;
  locationId?: string | null;
  roomName?: string | null;
  locationName?: string | null;
};

/** True when a value looks like a location/id key rather than a room name. */
export function isOpaqueRoomLabel(value: string | null | undefined): boolean {
  const t = String(value ?? "").trim();
  if (!t) return false;
  if (/^\d+$/.test(t)) return true;
  // UUID / hex-ish ids without whitespace
  if (/^[0-9a-f]{8,}(-[0-9a-f]+)*$/i.test(t) && !/\s/.test(t)) return true;
  // Internal hierarchy keys (loc:4, room_12)
  if (/^(?:loc|location|room|id)[:\_-]\d+$/i.test(t)) return true;
  return false;
}

export function isOpaqueSystemLabel(value: string | null | undefined): boolean {
  const t = String(value ?? "").trim();
  if (!t) return false;
  if (/^\d+$/.test(t)) return true;
  if (/^[0-9a-f]{8,}(-[0-9a-f]+)*$/i.test(t) && !/\s/.test(t)) return true;
  return false;
}

export function buildRoomIdToNameMap(
  lines: Array<{ locationId?: string | null; roomName?: string | null; locationName?: string | null }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const line of lines) {
    const id = String(line.locationId ?? "").trim();
    const name =
      String(line.roomName ?? "").trim() || String(line.locationName ?? "").trim() || "";
    if (!id || !name || isOpaqueRoomLabel(name)) continue;
    if (!map.has(id)) map.set(id, name);
  }
  return map;
}

export function buildSystemIdToNameMap(
  lines: Array<{ systemId?: string | null; systemName?: string | null }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const line of lines) {
    const id = String(line.systemId ?? "").trim();
    const name = String(line.systemName ?? "").trim();
    if (!id || !name || isOpaqueSystemLabel(name)) continue;
    if (!map.has(id)) map.set(id, name);
  }
  return map;
}

/** Extract placement from a D-Tools revise/staging line for VO persistence. */
export function placementFromDtoolsLine(line: DtoolsPlacementSource): VoPlacementFields {
  const systemId = String(line.systemId ?? "").trim() || null;
  const systemNameRaw = String(line.systemName ?? "").trim() || null;
  const systemName =
    systemNameRaw && !isOpaqueSystemLabel(systemNameRaw) ? systemNameRaw : null;

  const roomId = String(line.locationId ?? "").trim() || null;
  const roomNameRaw =
    String(line.roomName ?? "").trim() || String(line.locationName ?? "").trim() || null;
  const roomName = roomNameRaw && !isOpaqueRoomLabel(roomNameRaw) ? roomNameRaw : null;

  return {
    systemId,
    systemName: systemName || "Unassigned system",
    roomId,
    roomName,
    systemKey: systemName || "Unassigned system",
    roomKey: roomName,
  };
}

export type EnrichablePlacement = {
  systemId?: string | null;
  systemName?: string | null;
  roomId?: string | null;
  roomName?: string | null;
  systemKey?: string | null;
  roomKey?: string | null;
};

/**
 * Normalize legacy rows (roomKey = locationId) and resolve names from hierarchy maps.
 */
export function enrichVoPlacement<T extends EnrichablePlacement>(
  line: T,
  roomIdToName: Map<string, string> = new Map(),
  systemIdToName: Map<string, string> = new Map(),
): T & VoPlacementFields {
  const systemId =
    String(line.systemId ?? "").trim() ||
    (isOpaqueSystemLabel(line.systemKey) ? String(line.systemKey).trim() : "") ||
    null;

  let systemName =
    String(line.systemName ?? "").trim() ||
    (!isOpaqueSystemLabel(line.systemKey) ? String(line.systemKey ?? "").trim() : "") ||
    null;
  if ((!systemName || isOpaqueSystemLabel(systemName)) && systemId) {
    systemName = systemIdToName.get(systemId) ?? null;
  }
  if (!systemName || isOpaqueSystemLabel(systemName)) {
    systemName = "Unassigned system";
  }

  const roomId =
    String(line.roomId ?? "").trim() ||
    (isOpaqueRoomLabel(line.roomKey) ? String(line.roomKey).trim() : "") ||
    (isOpaqueRoomLabel(line.roomName) ? String(line.roomName).trim() : "") ||
    null;

  let roomName =
    String(line.roomName ?? "").trim() ||
    (!isOpaqueRoomLabel(line.roomKey) ? String(line.roomKey ?? "").trim() : "") ||
    null;
  if (roomName && isOpaqueRoomLabel(roomName)) roomName = null;
  if (!roomName && roomId) {
    roomName = roomIdToName.get(roomId) ?? null;
  }
  if (roomName && isOpaqueRoomLabel(roomName)) roomName = null;

  return {
    ...line,
    systemId,
    systemName,
    roomId,
    roomName,
    systemKey: systemName,
    roomKey: roomName,
  };
}

/** Customer-facing room label — never an opaque id. */
export function resolveVoRoomDisplayName(line: EnrichablePlacement): string {
  const enriched = enrichVoPlacement(line);
  if (enriched.roomName) return enriched.roomName;
  return "Room not available";
}

/** Customer-facing system label. */
export function resolveVoSystemDisplayName(line: EnrichablePlacement): string {
  const enriched = enrichVoPlacement(line);
  return enriched.systemName || "Unassigned system";
}

/** Hierarchy location key so named rooms without numeric ids still nest under System → Room. */
export function hierarchyLocationIdForPlacement(line: EnrichablePlacement): string | null {
  const enriched = enrichVoPlacement(line);
  if (enriched.roomId) return enriched.roomId;
  if (enriched.roomName) return `name:${enriched.roomName}`;
  return null;
}
